import { supabase } from './supabaseClient'

const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_URL + '/functions/v1'

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  // Supabase's Edge Function gateway requires BOTH headers — apikey to route/
  // authenticate the request at the gateway, Authorization for the actual
  // user JWT the function itself verifies. supabase-js's own .functions.invoke()
  // sends both automatically; a hand-rolled fetch() must do the same or the
  // gateway rejects the call with 401 before it ever reaches our function code.
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${session?.access_token ?? ''}`
  }
}

async function callFunction(name, { method = 'POST', body, headers = {} } = {}) {
  const auth = await authHeaders()
  const res = await fetch(`${FUNCTIONS_URL}/${name}`, {
    method,
    headers: { ...auth, ...(body ? { 'Content-Type': 'application/json' } : {}), ...headers },
    body: body ? JSON.stringify(body) : undefined
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `${name} failed (${res.status})`)
  return data
}

// ── Clients ──────────────────────────────────────────────────
export async function listClients() {
  const { data, error } = await supabase.from('clients').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return data
}
export async function createClient_(fields) {
  const { data, error } = await supabase.from('clients').insert(fields).select().single()
  if (error) throw error
  return data
}
export async function getClient(id) {
  const { data, error } = await supabase.from('clients').select('*').eq('id', id).single()
  if (error) throw error
  return data
}
export async function updateClient(id, fields) {
  const { error } = await supabase.from('clients').update(fields).eq('id', id)
  if (error) throw error
}
export async function deleteClient(id) {
  const { error } = await supabase.from('clients').delete().eq('id', id)
  if (error) throw error
}

// ── Projects ─────────────────────────────────────────────────
export async function listAllProjects() {
  // Powers the command palette's quick-jump search — capped, not paginated,
  // since this is one reseller's own project list, not a public directory.
  const { data, error } = await supabase.from('projects').select('*, clients(name)').order('updated_at', { ascending: false }).limit(200)
  if (error) throw error
  return data
}
export async function listProjectsForClient(clientId) {
  const { data, error } = await supabase.from('projects').select('*').eq('client_id', clientId).order('updated_at', { ascending: false })
  if (error) throw error
  return data
}
export async function createProject(clientId, title) {
  const { data, error } = await supabase.from('projects').insert({ client_id: clientId, title }).select().single()
  if (error) throw error
  return data
}
export async function getProject(id) {
  const { data, error } = await supabase.from('projects').select('*').eq('id', id).single()
  if (error) throw error
  return data
}
export async function saveProjectManifest(id, manifest, extra = {}) {
  const { error } = await supabase.from('projects').update({ manifest, ...extra }).eq('id', id)
  if (error) throw error
}
export async function deleteProject(id) {
  const { error } = await supabase.from('projects').delete().eq('id', id)
  if (error) throw error
}

// ── Storage (Edge Functions) ────────────────────────────────
export async function uploadAsset(projectId, key, blob) {
  const auth = await authHeaders()
  const res = await fetch(`${FUNCTIONS_URL}/upload-asset`, {
    method: 'POST',
    headers: { ...auth, 'x-project-id': projectId, 'x-asset-key': key, 'Content-Type': blob.type || 'application/octet-stream' },
    body: blob
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `upload-asset failed (${res.status})`)
  return data
}
export async function getSignedUrls(projectId, keys) {
  const data = await callFunction('get-signed-url', { body: { projectId, keys } })
  return data.urls
}
export async function publishProject(projectId) {
  return callFunction('publish-project', { body: { projectId } })
}

// ── Share links (time-limited, revocable — separate from Publish) ──
const VIEWER_BASE_URL = import.meta.env.VITE_VIEWER_BASE_URL || 'https://flipstudio-viewer.pages.dev'

export async function listShareLinks(projectId) {
  const { data, error } = await supabase.from('project_share_links').select('*').eq('project_id', projectId).order('created_at', { ascending: false })
  if (error) throw error
  return data
}
export async function createShareLink(projectId, { expiresAt, label } = {}) {
  // A random, unguessable token — not the project's own id, so a share link
  // can be individually revoked/expired without affecting the permanent
  // published URL or any other share link for the same project.
  const token = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '')
  const { data, error } = await supabase.from('project_share_links')
    .insert({ project_id: projectId, token, expires_at: expiresAt || null, label: label || null })
    .select().single()
  if (error) throw error
  return { ...data, url: `${VIEWER_BASE_URL}/s/${token}` }
}
export async function revokeShareLink(id) {
  const { error } = await supabase.from('project_share_links').update({ revoked_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}

// ── Google Drive backup connection ──────────────────────────
export async function getDriveConnection() {
  const { data, error } = await supabase.from('google_drive_connection').select('*').maybeSingle()
  if (error) throw error
  return data
}
export async function startDriveConnect(folderName) {
  const data = await callFunction('google-drive-oauth-start', { body: { folderName } })
  return data.url
}
export async function disconnectDrive() {
  return callFunction('google-drive-disconnect', { body: {} })
}
